import { LOCAL_STORAGE_KEYS } from '#root/utils/localStorage';
import { useEffect, useCallback, useRef } from 'react';

// Global counter to track active queries
let activeQueries = 0;
let originalTenantId: string | null = null;

const acquireTenant = (newTenantId: string) => {
    activeQueries++;
    if (activeQueries === 1) {
        // Only store original when first query starts
        originalTenantId = localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT_ID);
    }
    localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT_ID, newTenantId);
};

const releaseTenant = () => {
    activeQueries--;
    if (activeQueries === 0) {
        // Only restore when last query finishes
        if (originalTenantId) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT_ID, originalTenantId);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.TENANT_ID);
        }
        originalTenantId = null;
    }
};

export const withTenantId = async (operation: () => Promise<any>, tenantId: string) => {
    acquireTenant(tenantId);
    try {
        return await operation();
    } finally {
        releaseTenant();
    }
};

export const useWrappedQuery = <T extends { refetch: () => Promise<any> }>(
    queryHook: (params: any) => T,
    params: any,
    tenantId: string
) => {
    const queryResult = queryHook(params);
    const mounted = useRef(true);

    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    const wrappedRefetch = useCallback(async () => {
        if (!mounted.current) return;
        return await withTenantId(async () => {
            return await queryResult.refetch();
        }, tenantId);
    }, [queryResult.refetch, tenantId]);

    useEffect(() => {
        if (mounted.current) {
            wrappedRefetch();
        }
    }, [wrappedRefetch]);

    return {
        ...queryResult,
        refetch: wrappedRefetch
    };
};
